/**
 * Decisions the top-up modal makes.
 *
 * These lived as inline expressions inside TopUpModal, guarded by tests that
 * matched the SOURCE TEXT — `toContain('x.days >= 5 && x.days <= 10')`.
 * CLAUDE.md's testing doctrine names that exact shape as a failure mode: "a pin
 * that matches a spelling dies silently the day the spelling changes, and it
 * takes the rule with it." A prettier reformat or a rename would have disabled
 * the two rules that matter most on this surface while every test still passed.
 *
 * So they live here, as functions with behaviour a test can assert. Kept out of
 * ./burnRate.ts deliberately: that file is a byte-for-byte twin of the server's
 * engine, and these have no server half.
 */
import {
  AmountSuggestion,
  BurnRate,
  suggestAutoTopUp,
} from './burnRate';

/**
 * Which chip is pre-selected.
 *
 * The WEEK, never the largest amount on the ladder. Pre-selecting the biggest
 * number is how a customer ended up buying $500 he did not choose: the ladder's
 * top rung was the default, and the auto top-up amount beneath it carried the
 * same figure.
 *
 * Falls back to the second rung — still not the largest — when no rung lands in
 * the week band, and to the only rung when there is just one.
 */
export function pickDefaultChip(ladder: AmountSuggestion[]): AmountSuggestion | null {
  if (!Array.isArray(ladder) || ladder.length === 0) return null;
  const week = ladder.find((x) => x.days >= 5 && x.days <= 10);
  if (week) return week;
  return ladder[Math.min(1, ladder.length - 1)];
}

export interface AutoTopUpFieldSeed {
  thresholdUsd: number;
  topUpAmountUsd: number;
  /** True when these came from the account's own usage rather than a fallback. */
  personalised: boolean;
}

/**
 * What the auto top-up fields show when the modal opens.
 *
 * A STORED choice always wins. A suggestion may only fill fields the user has
 * never set — overwriting a figure someone deliberately chose would be its own
 * unexpected charge.
 */
export function seedAutoTopUpFields(input: {
  hasStoredSettings: boolean;
  stored: { thresholdUsd?: number | null; topUpAmountUsd?: number | null };
  burn: BurnRate | null;
  fallback: { thresholdUsd: number; topUpAmountUsd: number };
}): AutoTopUpFieldSeed {
  const { hasStoredSettings, stored, burn, fallback } = input;

  if (hasStoredSettings) {
    return {
      thresholdUsd:
        typeof stored.thresholdUsd === 'number' ? stored.thresholdUsd : fallback.thresholdUsd,
      topUpAmountUsd:
        typeof stored.topUpAmountUsd === 'number' ? stored.topUpAmountUsd : fallback.topUpAmountUsd,
      personalised: false,
    };
  }

  const suggested = burn && burn.confident ? suggestAutoTopUp(burn, fallback) : null;
  if (!suggested || !suggested.personalised) return { ...fallback, personalised: false };
  return {
    thresholdUsd: suggested.thresholdUsd,
    topUpAmountUsd: suggested.topUpAmountUsd,
    personalised: true,
  };
}
